import React, { Fragment } from 'react';
import moment from 'moment';
import { Area, AreaChart, Legend, XAxis, YAxis } from 'recharts';
import SFTooltip from '@material-ui/core/Tooltip';
import { Dropdown, List, ListItem, MenuButton, Tooltip, Icons } from '@silverfort/sf-components';
import { isEqual } from 'lodash';
import { MenuItemDivider } from '../DropdownMenu/MenuItem';
import ConfirmationMessage from '../Controls/ConfirmationMessage';

import DCStatusRow from './DCStatusRow';
import { bigNumberToText, downloadFile, httpGet, httpPost, nodeStateByID } from '../utils';
import { NODE_TYPE, NODES_STATES, PROTOCOL_DISPLAY_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { isRulesSyncedInNode } from '@silverfort-nodelibs/common/utils/common';

import wallIcon from './Images/sf_server.png';
import collapseIcon from '../common/Images/dropdown_expanded.png';
import expandIcon from '../common/Images/dropdown_collapsed.png';
import dcsActiveIcon from './Images/dcs_active_icon.png';
import dcsIcon from './Images/dcs_icon.png';
import metricsActiveIcon from './Images/metrics_active_icon.png';
import metricsIcon from './Images/metrics_icon.png';
import policySyncIcon from './Images/policy_sync.png';
import policyOutdatedIcon from './Images/policy_outdated.png';
import usersSyncIcon from './Images/users_sync.png';
import usersOutdatedIcon from './Images/users_outdated.png';
import { MmsCmsConnectivityStatus } from './SystemSettingsComponents/MmsCmsConnectivityStatus.js';
import policyAdSyncSyncedIcon from './Images/policy_ad_sync_synced.svg';
import policySyncedDisabled from './Images/Policies_sync_diabled.png';
import tokenStatusDisabled from './Images/Token-Status-disabled.png';

import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { Checkbox } from '@material-ui/core';

const TABS = {
    DCS: 0,
    METRICS: 1
};

const CustomLegend = () => {
    return (
        <div className="graphCustomLegend">
            <div className="kerberos">
                <p>&#x25A0;</p>
                <div>Kerberos</div>
            </div>
            <div className="ntlm">
                <p>&#x25A0;</p>
                <div>NTLM</div>
            </div>
            <div className="ldap">
                <p>&#x25A0;</p>
                <div>LDAP</div>
            </div>
            <div className="radius">
                <p>&#x25A0;</p>
                <div>RADIUS</div>
            </div>
            <div className="api">
                <p>&#x25A0;</p>
                <div>Silverfort API</div>
            </div>
        </div>
    );
};

const CustomTooltip = (props) => {
    if (!props) return null;

    const { payload } = props;

    if (!payload || payload.length === 0) {
        return null;
    }

    const tooltipRows = [];

    for (const protocolData of payload) {
        const protocolDisplayName = PROTOCOL_DISPLAY_NAMES[protocolData.name.toUpperCase()];

        tooltipRows.push(
            <div className="protocolRow" key={protocolData.name}>
                <div className={`count ${protocolData.name}`}>
                    <span>{`${protocolDisplayName} - `}</span>
                    {Number(protocolData.value).toLocaleString()}
                </div>
            </div>
        );
    }

    return (
        <div className="graphCustomTooltip">
            <div className="tooltipHeader">{moment.unix(payload[0].payload.timestamp).format('LT L')}</div>
            {tooltipRows}
        </div>
    );
};

const LicenseTooltip = ({ title, children }) => {
    return (
        <Tooltip className="flex flex-1 justify-center" title={title}>
            {children}
        </Tooltip>
    );
};

class NodeStatusRow extends React.Component {
    state = {
        data: {},
        IsDownloadingLog: false,
        IsDownloadError: false,
        IsSendingLog: false,
        IsSendingError: false,
        IsExpended: false,
        ActiveTab: TABS.DCS,
        SelectedMetricsDuration: 'hour',
        MetricsInterval: 'min',
        IsActionSent: false,
        ShowDownloadLogConfirmation: false,
        isGeneratingLog: this.props.NodeData.isGeneratingLog
    };

    validateLastLogBeforeDownload = () => {
        if (moment().diff(moment(this.props.NodeData.LastLog), 'days') > this.props.logsWarningDaysDiff) {
            this.setState({ ShowDownloadLogConfirmation: true });
        } else {
            this.DownloadWallLog();
        }
    };

    DownloadWallLog = () => {
        this.setState({ ShowDownloadLogConfirmation: false });
        downloadFile(`/Settings/DownloadNodeLog/${this.props.NodeData.id}`);
    };

    GenerateWallLog = async () => {
        this.setState({
            IsActionSent: true,
            IsActionError: false,
            isGeneratingLog: true
        });
        try {
            await httpGet(`/Settings/GenerateServerLogs/${this.props.NodeData.id}`);

            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
            setTimeout(() => {
                this.setState({ isGeneratingLog: false });
            }, 15000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true, isGeneratingLog: false });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };

    SendWallLog = () => {
        this.setState({ IsSendingLog: true, IsSendingLogError: false });
        httpGet(`/Settings/SendLogs/${this.props.NodeData.id}`)
            .then(() => {
                this.setState({ IsSendingLog: false, IsSendingLogError: false });
            })
            .catch((err) => {
                this.setState({ IsSendingLogError: true });
                setTimeout(() => this.setState({ IsSendingLog: false, IsSendingLogError: false }), 2000);
                console.error(err);
            });
    };

    RestartConnectivity = async () => {
        this.setState({ IsActionSent: true, IsActionError: false });

        try {
            await httpGet(`/Settings/RestartConnectivity/${this.props.NodeData.id}`);

            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };

    ReassignPort = async () => {
        this.setState({ IsActionSent: true, IsActionError: false });

        try {
            await httpGet(`/Settings/ReassignPort/${this.props.NodeData.id}/${NODE_TYPE.WALL}`);

            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };

    RebalanceAz = async () => {
        this.setState({ IsActionSent: true, IsActionError: false });
        try {
            const response = await httpGet(`/Settings/RebalanceAz/${this.props.NodeData.id}`);
            if (response && response.data) {
                const wallsInAz = response.data.wallsInAz || [];
                const disableTimeout = response.data.disableTimeout || 30 * 1000;
                this.props.disabledRebalanceAzForNodes(wallsInAz, true);
                setTimeout(() => {
                    this.setState({ IsActionSent: false });
                    this.props.disabledRebalanceAzForNodes(wallsInAz, false);
                }, disableTimeout);
            }
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };

    SendShutdownRequest = async () => {
        this.props.changeNodeDesiredState(this.props.NodeData.id, NODES_STATES.STANDBY.ID);
        this.setState({ IsActionSent: true });

        try {
            await httpPost(`/Settings/ShutdownNode/${this.props.NodeData.id}`);

            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };
    showRestartConfirmation = () => {
        this.setState({ ShowRestartConfirmation: true });
    };
    closeRestartConfirmation = () => {
        this.setState({ ShowRestartConfirmation: false });
    };
    sendRestartRequest = async () => {
        this.setState({
            IsActionSent: true,
            IsActionError: false,
            ShowRestartConfirmation: false
        });

        try {
            await httpPost(`/Settings/RestartNode/${this.props.NodeData.id}`);
            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };
    SendADSyncRequest = async () => {
        this.setState({ IsActionSent: true, IsActionError: false });
        try {
            await httpPost(`/Settings/SyncActiveDirectoryOnNode/${this.props.NodeData.id}`);
            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };
    SendPowerupRequest = async () => {
        this.props.changeNodeDesiredState(this.props.NodeData.id, NODES_STATES.ACTIVE.ID);
        this.setState({ IsActionSent: true, IsActionError: false });
        try {
            await httpPost(`/Settings/PowerupNode/${this.props.NodeData.id}`, {
                state: this.props.NodeData.status
            });
            setTimeout(() => {
                this.setState({ IsActionSent: false });
            }, 2000);
        } catch (err) {
            this.setState({ IsActionSent: false, IsActionError: true });
            setTimeout(() => {
                this.setState({ IsActionError: false });
            }, 2000);
            console.error(err);
        }
    };
    toggleExpender = () => {
        if (!this.state.IsExpended && !this.state.MetricsLoaded && !this.state.MetricsLoading) {
            this.getNodeMetrics({ value: this.state.SelectedMetricsDuration });
            this.setState({ IsExpended: !this.state.IsExpended });
        } else {
            this.setState({ IsExpended: !this.state.IsExpended });
        }
    };
    getNodeMetrics = async (e) => {
        const duration = e.value;
        this.setState({ MetricsLoading: true, SelectedMetricsDuration: duration });

        try {
            const response = await httpGet(`/Settings/GetWallMetrics/${this.props.NodeData.id}/${duration}`);
            this.setState({
                Metrics: response.data.Metrics,
                MetricsInterval: response.data.MetricsInterval,
                MetricsLoading: false,
                MetricsLoaded: true
            });
        } catch (err) {
            console.error(err);
            this.setState({ Metrics: [], MetricsLoading: false, MetricsLoaded: true });
        }
    };

    componentDidMount() {
        const locale = navigator.languages ? navigator.languages : navigator.language;
        moment.locale(locale);
    }

    render() {
        let nodeState = this.props.NodeData.status;

        if (nodeState !== this.props.NodeDesiredState && nodeState !== NODES_STATES.UNREACHABLE.ID) {
            if (
                this.props.NodeDesiredState === NODES_STATES.ACTIVE.ID &&
                nodeState !== NODES_STATES.ACTIVE_WITH_INCIDENTS.ID
            ) {
                nodeState = NODES_STATES.STARTING.ID;
            } else if (this.props.NodeDesiredState === NODES_STATES.STANDBY.ID) {
                nodeState = NODES_STATES.STOPPING.ID;
            }
        }

        const nodeStateData = nodeStateByID(nodeState);

        let nodePolicySync = null;
        let adsyncState = null;
        const { isPoliciesSynced, isAdSynced } = isRulesSyncedInNode(
            this.props.NodeData.data_versions,
            this.props.LatestVersions,
            this.props.isDuringPolicyVersionChange
        );

        if (!this.props.isPoliciesAndAdDataColumnsEnabledByLicense) {
            nodePolicySync = (
                <LicenseTooltip title="License required">
                    <img
                        title={`Policies: ${isPoliciesSynced ? 'Synced' : 'Outdated'}`}
                        src={policySyncedDisabled}
                        alt=""
                    />
                </LicenseTooltip>
            );

            adsyncState = (
                <LicenseTooltip title="License required">
                    <img
                        title={`Policy AD Data: ${isAdSynced ? 'Synced' : 'Outdated'}`}
                        src={isAdSynced ? policyAdSyncSyncedIcon : policyOutdatedIcon}
                        alt=""
                        height={30}
                        width={30}
                    />
                </LicenseTooltip>
            );
        } else {
            nodePolicySync = (
                <img
                    title={`Policies: ${isPoliciesSynced ? 'Synced' : 'Outdated'}`}
                    src={isPoliciesSynced ? policySyncIcon : policyOutdatedIcon}
                    alt=""
                />
            );

            adsyncState = (
                <i
                    className={`fa fa-refresh adsyncStatus ${isAdSynced ? 'synced' : 'outdated'}`}
                    aria-hidden="true"
                    title={`AD Data: ${isAdSynced ? 'Synced' : 'Outdated'}`}></i>
            );
        }

        let nodeUsersSync = null;
        if (!this.props.isMfaOrPasEnabledByLicense) {
            nodeUsersSync = (
                <LicenseTooltip title="License required">
                    <img src={tokenStatusDisabled} alt="" />
                </LicenseTooltip>
            );
        } else {
            if (
                !this.props.NodeData.data_versions ||
                !isEqual(this.props.NodeData.data_versions.userTokens, this.props.LatestVersions.UsersTokens)
            ) {
                nodeUsersSync = <img title={'Tokens: Outdated'} src={usersOutdatedIcon} alt="" />;
            } else {
                nodeUsersSync = <img title={'Tokens: Synced'} src={usersSyncIcon} alt="" />;
            }
        }

        let messagingServiceIcons = null;
        const hasCloudConnectivityField = 'cloud_connectivity' in this.props.NodeData;
        const shouldShowMMS = this.props.hasServiceBus === true;
        if (shouldShowMMS || hasCloudConnectivityField) {
            messagingServiceIcons = (
                <div className="flex ravenSync notification justify-center">
                    {shouldShowMMS && (
                        <MmsCmsConnectivityStatus
                            name={'MMS'}
                            offlineMode={this.props.offlineMode}
                            isActive={!!this.props.NodeData.raven_connectivity}
                            showText={false}
                            showTooltip={true}
                            className="w-fit"
                        />
                    )}
                    {hasCloudConnectivityField && (
                        <MmsCmsConnectivityStatus
                            name={'CMS'}
                            offlineMode={this.props.offlineMode}
                            isActive={!!this.props.NodeData.cloud_connectivity}
                            showText={false}
                            showTooltip={true}
                            className="w-fit"
                        />
                    )}
                </div>
            );
        }

        let lastLog = 'N/A';
        if (this.props.NodeData.LastLog) {
            const logTime = moment(this.props.NodeData.LastLog).local();
            if (logTime.isValid()) {
                lastLog = logTime.format('L') + ', ' + logTime.format('LTS');
            }
        }

        let connectedDCs = 0;
        const dcsRows = [];

        if (this.props.NodeData.dcs && this.props.NodeData.dcs.length > 0) {
            this.props.NodeData.dcs = this.props.NodeData.dcs.sort(function (a, b) {
                try {
                    if (a.state !== b.state) {
                        return a.state ? -1 : 1;
                    }
                    if (a.dc_name < b.dc_name) {
                        return -1;
                    }
                    if (a.dc_name > b.dc_name) {
                        return 1;
                    }
                    return 0;
                } catch (ex) {
                    return 0;
                }
            });

            for (const [index, dcData] of this.props.NodeData.dcs.entries()) {
                dcData.index = index + 1;
                dcData.ipAddress = '';
                if (this.props.domainControllers) {
                    this.props.domainControllers.some((domainController) => {
                        if (domainController.guid === dcData.dc_guid) {
                            dcData.ipAddress = domainController.ipAddress;
                            return true;
                        }
                        return false;
                    });
                }

                const dcName = dcData.dc_name || dcData.display_name;
                if (dcData.state) {
                    connectedDCs++;
                    dcsRows.push(
                        <DCStatusRow
                            systemMode={this.props.systemMode}
                            key={`dc_${index}`}
                            data={dcData}
                            name={dcName}
                            nodeID={this.props.NodeData.id}
                            HideExpender={true}
                            csrf={this.props.csrf}
                            allDcsDuringRecover={this.props.allDcsDuringRecover}
                            isCloudOnly={this.props.isCloudOnly}
                        />
                    );
                }
            }
        }

        const isOnPrem = this.props.systemMode === SYSTEM_MODES.ON_PREM;

        return (
            <div className={'serverRow nodeServerRow' + (this.props.isHeader ? ' tableHeader' : '')}>
                <div className={'title'} onClick={this.props.isHeader ? () => {} : this.toggleExpender}>
                    <div className="leftSide">
                        {this.props.isHeader && (
                            <Fragment>
                                <Checkbox
                                    disableRipple={true}
                                    checked={this.props.isAllNodesSelected}
                                    onChange={() => this.props.checkAllNodes()}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                    style={{
                                        transform: 'scale(0.667)'
                                    }}
                                    className="pl-0"
                                />
                                <div className="tableHeaderTitle notification name">Name</div>
                                <div className="tableHeaderTitle notification status">Status</div>
                            </Fragment>
                        )}
                        {!this.props.isHeader && (
                            <Fragment>
                                <Checkbox
                                    disableRipple={true}
                                    checked={this.props.isSelected}
                                    onChange={() => {
                                        this.props.checkNode();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    color="primary"
                                    style={{
                                        transform: 'scale(0.667)'
                                    }}
                                    className="pl-0"
                                />
                                {this.state.IsExpended && <img className="toggleIcon" src={collapseIcon} alt="" />}
                                {!this.state.IsExpended && <img className="toggleIcon" src={expandIcon} alt="" />}
                                <img src={wallIcon} alt="" />
                                <div className="wallHeader" title={this.props.NodeDisplayName}>
                                    <div className="nodeNameContainer">
                                        <div className="nodeDisplayName">{this.props.NodeDisplayName}</div>
                                        {this.props.NodeData.hasWarningDot && (
                                            <span className="orange-alert-dot alert-dot small-alert-dot"></span>
                                        )}
                                    </div>

                                    {this.props.NodeData.ipAddress && (
                                        <div className="ipAddress" title={this.props.NodeData.ipAddress}>
                                            {this.props.NodeData.ipAddress}
                                        </div>
                                    )}
                                </div>
                                <div className={`wallState ${nodeStateData.TEXT}`}>{nodeStateData.DISPLAY}</div>
                                {isOnPrem && !Object.keys(this.props.NodeData.domainsConfiguration).length && (
                                    <SFTooltip
                                        title={'Node not assigned to Domains'}
                                        arrow={true}
                                        placement="right"
                                        classes={{ tooltip: 'moreInfoIcon-tooltip' }}>
                                        <div className="moreInfoIcon"></div>
                                    </SFTooltip>
                                )}
                            </Fragment>
                        )}
                    </div>
                    <div className="rightSide">
                        {this.props.isHeader && (
                            <Fragment>
                                <div className={'connectedDCs notification tableHeaderTitle'}>Connected DCs</div>
                                <div className={'policySync notification tableHeaderTitle'}>Policies</div>
                                <div className={'adDataSync notification tableHeaderTitle min-w-[127px]'}>
                                    Policy AD Data
                                </div>
                                <div className={'usersSync notification tableHeaderTitle'}>Tokens Status</div>
                                <div className={'ravenSync notification tableHeaderTitle'}>Messaging Service</div>
                                <div className={'lastGeneratedLog notification timestamp tableHeaderTitle'}>
                                    Last Generated Log
                                </div>
                                <div className={'actions notification tableHeaderTitle'}>Actions</div>
                            </Fragment>
                        )}
                        {!this.props.isHeader && (
                            <Fragment>
                                <div className="connectedDCs notification">
                                    {connectedDCs}
                                    <span>DCs</span>
                                </div>
                                <div className="policySync notification">{nodePolicySync}</div>
                                <div className="adDataSync notification min-w-[127px]">{adsyncState}</div>
                                <div className="usersSync notification">{nodeUsersSync}</div>
                                {messagingServiceIcons || <div className="ravenSync notification w-64"></div>}
                                <div className="lastGeneratedLog timestamp">
                                    {(this.state.isGeneratingLog || this.props.NodeData.isGeneratingLog) && (
                                        <i className={'fa fa-refresh fa-spin'} aria-hidden="true"></i>
                                    )}
                                    <span>{lastLog}</span>
                                </div>
                                <div
                                    className="actions"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}>
                                    <MenuButton
                                        className={`nodeActionsButton left-[-80px] text-left`}
                                        buttonStyle="PrimarySolid"
                                        disabled={
                                            this.state.IsActionSent || nodeStateData.ID === NODES_STATES.UNREACHABLE.ID
                                        }
                                        text={this.state.IsActionSent ? 'Sent!' : 'Actions'}
                                        iconRight={this.state.IsActionSent ? undefined : Icons.ChevronDown}
                                        id={'action-dropdown' + this.props.NodeData.id}
                                        content={
                                            <List>
                                                {isOnPrem && this.props.NodeData.ADSyncEnabled && (
                                                    <>
                                                        <ListItem
                                                            title="AD Sync"
                                                            onClick={this.SendADSyncRequest}
                                                            disabled={!this.props.canPreformAdsync}
                                                        />
                                                        <MenuItemDivider />
                                                    </>
                                                )}
                                                {isOnPrem && (
                                                    <>
                                                        <ListItem
                                                            title="Generate log"
                                                            onClick={this.GenerateWallLog}
                                                            disabled={!this.props.isEditor}
                                                        />
                                                        <ListItem
                                                            title="Download log"
                                                            onClick={this.validateLastLogBeforeDownload}
                                                            disabled={
                                                                !this.props.NodeData.LastLog ||
                                                                lastLog === 'N/A' ||
                                                                !this.props.isEditor
                                                            }
                                                        />
                                                    </>
                                                )}
                                                <ListItem
                                                    title={
                                                        this.props.sendingStatus || this.state.IsSendingLog
                                                            ? 'Sending...'
                                                            : 'Send log'
                                                    }
                                                    onClick={this.SendWallLog}
                                                    disabled={
                                                        this.props.sendingStatus ||
                                                        this.state.IsSendingLog ||
                                                        !this.props.isEditor
                                                    }
                                                />
                                                {isOnPrem && (
                                                    <ListItem
                                                        title="Restart connectivity"
                                                        onClick={this.RestartConnectivity}
                                                        disabled={!this.props.isEditor}
                                                    />
                                                )}
                                                {isOnPrem && (
                                                    <ListItem
                                                        title="Reassign Port"
                                                        onClick={this.ReassignPort}
                                                        disabled={!this.props.isEditor}
                                                    />
                                                )}
                                                <ListItem
                                                    title="Balance AZ"
                                                    onClick={this.RebalanceAz}
                                                    disabled={
                                                        !this.props.isEditor || this.props.rebalanceAzActionDisabled
                                                    }
                                                />
                                                {nodeStateData.ID !== NODES_STATES.UNREACHABLE.ID && (
                                                    <MenuItemDivider />
                                                )}
                                                {nodeStateData.ID !== NODES_STATES.UNREACHABLE.ID && (
                                                    <ListItem
                                                        title="Restart node"
                                                        onClick={this.showRestartConfirmation}
                                                        disabled={
                                                            !this.props.isEditor ||
                                                            !Object.keys(this.props.NodeData.domainsConfiguration)
                                                                .length
                                                        }
                                                    />
                                                )}
                                                {(nodeStateData.ID === NODES_STATES.ACTIVE.ID ||
                                                    nodeStateData.ID === NODES_STATES.ACTIVE_WITH_INCIDENTS.ID) && (
                                                    <ListItem
                                                        title="Recover node"
                                                        disabled={
                                                            nodeStateData.ID === NODES_STATES.ACTIVE.ID ||
                                                            !this.props.isEditor
                                                        }
                                                        onClick={this.SendPowerupRequest}
                                                    />
                                                )}
                                                {(nodeStateData.ID === NODES_STATES.ACTIVE.ID ||
                                                    nodeStateData.ID === NODES_STATES.ACTIVE_WITH_INCIDENTS.ID) && (
                                                    <ListItem
                                                        title="Stop node"
                                                        onClick={this.SendShutdownRequest}
                                                        disabled={!this.props.isEditor}
                                                    />
                                                )}
                                                {nodeStateData.ID === NODES_STATES.STANDBY.ID && (
                                                    <ListItem
                                                        title="Start node"
                                                        disabled={
                                                            this.props.LicenseExpiresInHours <= 0 ||
                                                            (isOnPrem &&
                                                                !Object.keys(this.props.NodeData.domainsConfiguration)
                                                                    .length) ||
                                                            !this.props.isEditor
                                                        }
                                                        onClick={this.SendPowerupRequest}
                                                    />
                                                )}
                                            </List>
                                        }
                                    />
                                </div>
                            </Fragment>
                        )}
                    </div>
                </div>
                {this.state.IsExpended && (
                    <div className="details">
                        <div className="tabs">
                            <div
                                className={'dcsTab' + (this.state.ActiveTab === TABS.DCS ? ' selected' : '')}
                                onClick={() => this.setState({ ActiveTab: TABS.DCS })}>
                                {this.state.ActiveTab === TABS.DCS && <img src={dcsActiveIcon} alt="" />}
                                {this.state.ActiveTab !== TABS.DCS && <img src={dcsIcon} alt="" />}
                                <span>Domain</span>
                                <span>Controllers</span>
                            </div>
                            <div
                                className={'metricsTab' + (this.state.ActiveTab === TABS.METRICS ? ' selected' : '')}
                                onClick={() => this.setState({ ActiveTab: TABS.METRICS })}>
                                {this.state.ActiveTab === TABS.METRICS && <img src={metricsActiveIcon} alt="" />}
                                {this.state.ActiveTab !== TABS.METRICS && <img src={metricsIcon} alt="" />}
                                <span>Metrics</span>
                            </div>
                        </div>
                        {this.state.ActiveTab === TABS.DCS && (
                            <div className="domainControllers light-scrollbar">
                                {dcsRows.length > 0 && dcsRows}
                                {dcsRows.length === 0 && (
                                    <div className="banner">
                                        <span>No Connected DCs</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {this.state.ActiveTab === TABS.METRICS && (
                            <div className="metrics">
                                {!this.state.Metrics && (
                                    <div className="banner">
                                        <span>Loading...</span>
                                    </div>
                                )}
                                <div className="subTitle">
                                    <div>Authentications by protocol:</div>
                                    <Dropdown
                                        name="form-field-name"
                                        className={`w-[140px] mx-1`}
                                        items={[
                                            { value: 'hour', title: `Last hour` },
                                            { value: 'day', title: `Last day` },
                                            { value: 'week', title: `Last week` }
                                        ]}
                                        fullMenuWidth={140}
                                        selectedItems={this.state.SelectedMetricsDuration}
                                        variant="trigger"
                                        onChange={(value) => {
                                            this.getNodeMetrics({ value });
                                        }}
                                    />
                                </div>
                                {this.state.Metrics && this.state.Metrics.length === 0 && (
                                    <div className="banner">
                                        <span>No Data Found</span>
                                    </div>
                                )}
                                {this.state.Metrics && this.state.Metrics.length > 0 && (
                                    <div>
                                        <div className="charts">
                                            <AreaChart
                                                width={700}
                                                height={200}
                                                data={this.state.Metrics}
                                                margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                                                <XAxis
                                                    dataKey="timestamp"
                                                    type="number"
                                                    allowDataOverflow={true}
                                                    tickFormatter={(timeStr) =>
                                                        this.state.SelectedMetricsDuration !== 'week'
                                                            ? moment.unix(timeStr).format('HH:mm')
                                                            : moment.unix(timeStr).format('L')
                                                    }
                                                    tickCount={this.state.Metrics.length}
                                                    interval={'preserveStartEnd'}
                                                    domain={['dataMin', 'dataMax']}
                                                    label={{ value: 'Time', position: 'insideBottomRight', dy: 10 }}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, (dataMax) => Math.floor(dataMax * 1.3)]}
                                                    width={50}
                                                    tickFormatter={(tickValue) => bigNumberToText(tickValue)}
                                                    label={{
                                                        value: `Authentications \\ ${this.state.MetricsInterval}`,
                                                        position: 'insideBottomLeft',
                                                        angle: -90,
                                                        dx: 5,
                                                        dy: -10
                                                    }}
                                                />
                                                <Tooltip cursor={false} content={<CustomTooltip />} />
                                                <Legend content={<CustomLegend />} />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="kerberos"
                                                    stroke={'none'}
                                                    fill="#188ded"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="ntlm"
                                                    stroke={'none'}
                                                    fill="#1eb04b"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="ldap"
                                                    stroke={'none'}
                                                    fill="#f12047"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="radius"
                                                    stroke={'none'}
                                                    fill="#848282"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="saml"
                                                    stroke={'none'}
                                                    fill="#673ab7"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                                <Area
                                                    type="natural"
                                                    isAnimationActive={true}
                                                    animationDuration={300}
                                                    stackId="1"
                                                    dataKey="api"
                                                    stroke={'none'}
                                                    fill="#fb9e44"
                                                    dot={false}
                                                    activeDot={{
                                                        stroke: 'transparent',
                                                        fill: '#000',
                                                        strokeWidth: 1,
                                                        r: 2
                                                    }}
                                                />
                                            </AreaChart>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {this.state.ShowRestartConfirmation && (
                    <ConfirmationMessage
                        title={'Restart Node'}
                        body={`Are you sure you want to restart this node?`}
                        approveText={'Restart'}
                        approveStyle={'danger'}
                        OnCancel={this.closeRestartConfirmation}
                        OnApprove={this.sendRestartRequest}
                    />
                )}
                {this.state.ShowDownloadLogConfirmation && (
                    <ConfirmationMessage
                        title={'Download log'}
                        body={`The last log was generated more than ${this.props.logsWarningDaysDiff} day${
                            this.props.logsWarningDaysDiff > 1 ? 's' : ''
                        } ago. Are you sure you want to download?`}
                        approveText={'Download Log'}
                        OnCancel={() => this.setState({ ShowDownloadLogConfirmation: false })}
                        OnApprove={() => this.DownloadWallLog()}
                    />
                )}
            </div>
        );
    }
}

export default NodeStatusRow;
